<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import FormSection from '@/Components/FormSection.vue';
import InputLabel from '@/Components/InputLabel.vue';
import TextInput from '@/Components/TextInput.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import { useForm } from '@inertiajs/inertia-vue3';
import { ref, computed } from 'vue';
import { Inertia } from '@inertiajs/inertia';
import DialogModal from '@/Components/DialogModal.vue';
import { useTenantTextRules } from '@/helpers'


const props = defineProps({
  item: Object,
  type: String
});
const { resolveTextRule } = useTenantTextRules();

const sizeScaleLabel = computed(() =>
  resolveTextRule('size_scale.label', 'Size Scale', 'database.size_scale.label')
);
const pageTitle = computed(() =>
  form.id
    ? resolveTextRule('database.size.edit_title', `Edit ${sizeScaleLabel.value}`, 'database.size.edit_title')
    : resolveTextRule('database.size.create_title', `Create ${sizeScaleLabel.value}`, 'database.size.create_title')
);

console.log('Props:', props);

const form = useForm({
  _method: props.item.id ? 'PUT' : 'POST',
  scaleName: props.item.scaleName || '',
  opt1Name: props.item.opt1Name || '',
  opt2Name: props.item.opt2Name || '',
  mainSizes: props.item.mainSizes ?? [],
  opt1Sizes: props.item.opt1Sizes ?? [],
  opt2Sizes: props.item.opt2Sizes ?? [],
  id: props.item.id || null
});

const saveForm = () => {
  const routeName = form.id
    ? route('admin.database.size-scales.update', form.id)
    : route('admin.database.size-scales.store');

  form.post(routeName, { preserveScroll: true });
};

const backRoute = computed(() =>
  route('admin.database.index', { tab: 'size' })
);

const addRow = (listName) => {
  form[listName].push({ size: '', order: null });
};

const removeRowLocally = (listName, index) => {
  form[listName].splice(index, 1);
};

/* ---------- Confirm modal flow ---------- */
const confirmOpen = ref(false);
const pending = ref({ field: null, listName: null, index: null, rowId: null });

const askDelete = (field, listName, index, rowId) => {
  if (!rowId) {
    removeRowLocally(listName, index);
    return;
  }

  pending.value = { field, listName, index, rowId };
  confirmOpen.value = true;
};

const doDelete = () => {
  confirmOpen.value = false;

  Inertia.post(
    route('admin.database.size-scales.clear-slot'),
    { id: pending.value.rowId, field: pending.value.field },
    {
      preserveScroll: true,
      preserveState: false,
      onSuccess: () => {
        removeRowLocally(pending.value.listName, pending.value.index);
      },
    }
  );
};
</script>

<template>
  <AppLayout :title="pageTitle">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        {{ pageTitle }}
      </h2>
    </template>

    <div class="max-w-4xl mx-auto py-10 sm:px-6 lg:px-8">
      <FormSection gridClasses="grid-cols-1" :noGrid="true">
        <template #title>
          <a :href="backRoute">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>

        <template #form>
          <div class="flex flex-col gap-6">
            <div>
              <h3 class="font-bold text-lg mb-2">{{ sizeScaleLabel }}</h3>
              <p class="text-red-600 text-sm mb-3">
                All changes on this page will affect all current inventory.
              </p>
              <InputLabel value="Name" />
              <TextInput v-model="form.scaleName" class="w-full mt-1" />
            </div>

            <div>
              <InputLabel value="Main Sizes" />
              <div class="space-y-2">
                <div v-for="(row, index) in form.mainSizes" :key="index" class="flex gap-2">
                  <TextInput v-model="row.size" placeholder="Size" class="w-1/2" />
                  <TextInput v-model.number="row.order" placeholder="Order" type="number" class="w-1/2" />
                  <button @click.prevent="askDelete('main', 'mainSizes', index, row.id)" class="text-red-600">
                    x
                  </button>
                </div>
                <button @click.prevent="addRow('mainSizes')" class="text-blue-600">+ Add Row</button>
              </div>
            </div>

            <div>
              <h3 class="font-bold text-lg mb-2">Row</h3>
              <InputLabel value="Row Name" />
              <TextInput v-model="form.opt1Name" class="w-full mt-1" />
            </div>

            <div>
              <InputLabel value="Row Sizes" />
              <div class="space-y-2">
                <div v-for="(row, index) in form.opt1Sizes" :key="index" class="flex gap-2">
                  <TextInput v-model="row.size" placeholder="Size" class="w-1/2" />
                  <TextInput v-model.number="row.order" placeholder="Order" type="number" class="w-1/2" />
                  <button @click.prevent="askDelete('opt1', 'opt1Sizes', index, row.id)" class="text-red-600">
                    x
                  </button>
                </div>
                <button @click.prevent="addRow('opt1Sizes')" class="text-blue-600">+ Add Row</button>
              </div>
            </div>

            <div>
              <h3 class="font-bold text-lg mb-2">Column</h3>
              <InputLabel value="Column Name" />
              <TextInput v-model="form.opt2Name" class="w-full mt-1" />
            </div>

            <div>
              <InputLabel value="Column Name" />
              <div class="space-y-2">
                <div v-for="(row, index) in form.opt2Sizes" :key="index" class="flex gap-2">
                  <TextInput v-model="row.size" placeholder="Size" class="w-1/2" />
                  <TextInput v-model.number="row.order" placeholder="Order" type="number" class="w-1/2" />
                  <button @click.prevent="askDelete('opt2', 'opt2Sizes', index, row.id)" class="text-red-600">
                    x
                  </button>
                </div>
                <button @click.prevent="addRow('opt2Sizes')" class="text-blue-600">+ Add Row</button>
              </div>
            </div>
          </div>
        </template>

        <template #actions>
          <PrimaryButton @click.prevent="saveForm">
            {{ form.id ? 'Update' : 'Create' }}
          </PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>

  <DialogModal :show="confirmOpen" @close="confirmOpen = false">
    <template #title>Confirm delete</template>
    <template #content>
      This will clear the selected size (set it to null) without changing positions.
    </template>
    <template #footer>
      <SecondaryButton @click="confirmOpen = false">Cancel</SecondaryButton>
      <PrimaryButton class="ml-2" @click="doDelete">Confirm</PrimaryButton>
    </template>
  </DialogModal>
</template>
